import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "../lib/api";
import { Card } from "./ui";

// 一覧・詳細で必須の4状態（読込中・エラー・権限なし・0件）を1箇所で（[19] §5）。
export function Loading() {
  return (
    <div className="flex items-center justify-center py-16 text-brand">
      <span className="animate-pulse text-sm font-bold">読み込み中…</span>
    </div>
  );
}

export function EmptyState({ message }: { message: string }) {
  return (
    <Card className="text-center text-slate-500">
      <p className="py-6 text-sm">{message}</p>
    </Card>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  const isApi = error instanceof ApiError;
  const forbidden = isApi && (error.status === 403 || error.status === 401);
  if (forbidden) {
    return (
      <Card className="text-center">
        <p className="py-6 text-sm font-bold text-slate-500">
          このページを表示する権限がありません。
        </p>
      </Card>
    );
  }
  const message = isApi ? error.message : "エラーが発生しました。時間をおいて再度お試しください。";
  return (
    <Card className="text-center">
      <p className="py-6 text-sm font-bold text-rose-500">{message}</p>
    </Card>
  );
}

// クエリ結果を4状態でハンドリングする薄いラッパ。
export function QueryView<T>({
  query,
  emptyWhen,
  emptyMessage = "データがありません。",
  children,
}: {
  query: UseQueryResult<T>;
  emptyWhen?: (data: T) => boolean;
  emptyMessage?: string;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorState error={query.error} />;
  const data = query.data as T;
  if (emptyWhen && emptyWhen(data)) return <EmptyState message={emptyMessage} />;
  return <>{children(data)}</>;
}
